/** Orb pick bonus game (3 picks) for jackpot tiers */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { SpineManager } from '../animations/SpineManager';
import { Logger } from '../utils/Logger';
import {
  JACKPOT_PICK_BASE_WEIGHTS,
  JACKPOT_PICK_TIER_MULTIPLIERS,
  JACKPOT_PICK_TIER_NAMES,
  JACKPOT_PICK_TIER_COLORS,
  JACKPOT_PICK_12_WEIGHTS,
  JACKPOT_PICK_3_WEIGHTS,
  JACKPOT_PICK_TIERS,
  ESCALATING_WILD_WEIGHTS,
} from '../config/FeatureConfig';

const logger = new Logger('JackpotPick');

export interface JackpotPickResult {
  picks: number[];        // 3 tier values (6,7,8, or 9)
  matchedTier: number;    // highest matched pair tier (0 if no pair)
  prize: number;          // total prize amount in currency
}

export interface JackpotPickState {
  picks: number[];
  highestPair: string | null;
  prizeMultiplier: number;
  isActive: boolean;
}

// ─── Weighted random helper ──────────────────────────────────────────────────

/** Pick an index from a weights array using uniform random */
function weightedRandomIndex(weights: readonly number[]): number {
  const total = weights.reduce((s, w) => s + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r <= 0) return i;
  }
  return weights.length - 1;
}

/** Check a single [triggerWeight, noTriggerWeight] pair */
function checkWeight(pair: readonly [number, number]): boolean {
  const total = pair[0] + pair[1];
  return Math.random() * total < pair[0];
}

// ─── Core pick logic (pure, no UI) ──────────────────────────────────────────

/** Generate 3 picks and determine the result */
export function executePicks(): { picks: number[]; matchedTier: number } {
  const picks: number[] = [];

  // Picks 1 & 2: equal probability across tiers
  for (let i = 0; i < 2; i++) {
    const idx = weightedRandomIndex(JACKPOT_PICK_12_WEIGHTS);
    picks.push(JACKPOT_PICK_TIERS[idx]);
  }

  // Pick 3: heavily weighted
  const idx3 = weightedRandomIndex(JACKPOT_PICK_3_WEIGHTS);
  picks.push(JACKPOT_PICK_TIERS[idx3]);

  // Determine highest matched pair
  const matchedTier = findHighestPair(picks);

  return { picks, matchedTier };
}

/** Find the highest tier that appears at least twice in the picks array */
function findHighestPair(picks: number[]): number {
  const counts: Record<number, number> = {};
  for (const p of picks) {
    counts[p] = (counts[p] || 0) + 1;
  }

  let best = 0;
  for (const tier of JACKPOT_PICK_TIERS) {
    if ((counts[tier] || 0) >= 2 && tier > best) {
      best = tier;
    }
  }
  return best;
}

// ─── Feature class ──────────────────────────────────────────────────────────

/** Manages the Jackpot Pick bonus game */
export class JackpotPickFeature {
  private _state: JackpotPickState = {
    picks: [],
    highestPair: null,
    prizeMultiplier: 0,
    isActive: false,
  };

  /** Spine instances for the bonus pick scene */
  private _spineBg: Spine | null = null;
  private _spineOrbs: Spine | null = null;

  get state(): Readonly<JackpotPickState> {
    return this._state;
  }

  get isActive(): boolean {
    return this._state.isActive;
  }

  // ─── Trigger checks ─────────────────────────────────────────────────────

  /**
   * Determine whether the Jackpot Pick should trigger.
   * Requires a WILD to have landed on the grid.
   *
   * Base game: check 3 independent weight tiers — any hit triggers.
   * Free game: use escalating wild weights (same table, indexed by spinNumber).
   */
  static shouldTrigger(
    hasWild: boolean,
    isFreeGame: boolean,
    freeSpinNumber?: number,
  ): boolean {
    if (!hasWild) return false;

    if (!isFreeGame) {
      // Base game: 3 independent tier checks
      for (const weights of JACKPOT_PICK_BASE_WEIGHTS) {
        if (checkWeight(weights)) {
          logger.info('Jackpot Pick triggered (base game)');
          return true;
        }
      }
      return false;
    }

    // Free game: use escalating weights indexed by spin number
    const spinIdx = Math.min(
      Math.max((freeSpinNumber ?? 1) - 1, 0),
      ESCALATING_WILD_WEIGHTS.length - 1,
    );
    const weights = ESCALATING_WILD_WEIGHTS[spinIdx];
    const triggered = checkWeight(weights);
    if (triggered) {
      logger.info(`Jackpot Pick triggered (free game spin ${freeSpinNumber})`);
    }
    return triggered;
  }

  // ─── Start feature (with UI) ────────────────────────────────────────────

  /**
   * Run the full Jackpot Pick bonus with interactive UI.
   * Adds a full-screen overlay to `parentContainer`, waits for 3 player clicks,
   * reveals tiers, shows result, then cleans up.
   *
   * @returns The prize amount (totalBet * tier multiplier), or 0 if no pair.
   */
  async start(
    parentContainer: Container,
    designW: number,
    designH: number,
    totalBet: number,
  ): Promise<number> {
    this._state.isActive = true;
    this._state.picks = [];
    this._state.highestPair = null;
    this._state.prizeMultiplier = 0;

    // Pre-compute result server-side style (picks are predetermined)
    const result = executePicks();
    logger.info(
      `Jackpot Pick result: picks=[${result.picks}], matchedTier=${result.matchedTier}`,
    );

    // Calculate prize
    const multiplier = result.matchedTier > 0
      ? (JACKPOT_PICK_TIER_MULTIPLIERS[result.matchedTier] ?? 0)
      : 0;
    const prize = multiplier * totalBet;

    this._state.picks = result.picks;
    this._state.highestPair = result.matchedTier > 0
      ? JACKPOT_PICK_TIER_NAMES[result.matchedTier]
      : null;
    this._state.prizeMultiplier = multiplier;

    // ── Build UI overlay ───────────────────────────────────────────────────
    const overlay = new Container();
    overlay.zIndex = 2000;
    parentContainer.addChild(overlay);

    // Dark backdrop — use oversized rect so rotation mid-overlay won't expose gaps.
    // We draw a square whose side = max(designW, designH) and offset it so it
    // stays centred regardless of which dimension is larger after rotation.
    const backdropSize = Math.max(designW, designH);
    const backdrop = new Graphics();
    backdrop.rect(
      (designW - backdropSize) / 2,
      (designH - backdropSize) / 2,
      backdropSize,
      backdropSize,
    );
    backdrop.fill({ color: 0x000000, alpha: 0.75 });
    overlay.addChild(backdrop);

    // Responsive scale factors
    // NOTE: Orbs, text, and Spine positions are computed from the snapshot
    // designW/designH and are NOT re-laid-out on device rotation. This is an
    // accepted limitation — the pick interaction lasts <10 seconds and rotation
    // mid-pick is extremely unlikely. The backdrop above IS oversized to cover
    // any orientation change.
    const bgScale = Math.min(designW / 1920, designH / 1080);
    const uiScale = Math.min(1, designW / 1920);

    // Spine background animation (bonus_pick_background)
    const sm = SpineManager.getInstance();
    if (sm.isLoaded('bonus_pick_background')) {
      const bg = sm.createInstance('bonus_pick_background');
      if (bg) {
        this._spineBg = bg;
        bg.x = designW / 2;
        bg.y = designH / 2;
        bg.scale.set(bgScale);
        overlay.addChild(bg);
        sm.playStartThenLoop(bg, 'background_appear', 'background_loop').catch(() => {});
      }
    }

    // Title
    const titleText = new Text({
      text: 'JACKPOT PICK',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: Math.round(52 * uiScale),
        fill: 0xf7b733,
        stroke: { color: 0x000000, width: Math.round(5 * uiScale) },
        dropShadow: { color: 0x000000, blur: 8, distance: 4, angle: Math.PI / 4 },
      }),
    });
    titleText.anchor.set(0.5, 0.5);
    titleText.x = designW / 2;
    titleText.y = designH * 0.18;
    overlay.addChild(titleText);

    // Subtitle
    const subtitleText = new Text({
      text: 'Pick 3 orbs to reveal jackpot tiers!',
      style: new TextStyle({
        fontFamily: 'Arial, Helvetica',
        fontSize: Math.round(24 * uiScale),
        fill: 0xcccccc,
      }),
    });
    subtitleText.anchor.set(0.5, 0.5);
    subtitleText.x = designW / 2;
    subtitleText.y = designH * 0.26;
    overlay.addChild(subtitleText);

    // ── Create 3 orbs ──────────────────────────────────────────────────────
    const orbRadius = Math.round(70 * uiScale);
    const orbSpacing = Math.min(200, designW * 0.18);
    const orbStartX = designW / 2 - orbSpacing;
    const orbY = designH * 0.48;

    const orbContainers: Container[] = [];
    const orbGraphics: Graphics[] = [];
    const orbLabels: Text[] = [];
    let picksRevealed = 0;

    // Promise that resolves when all 3 picks are done
    const allPicksDone = new Promise<void>((resolveAllPicks) => {
      for (let i = 0; i < 3; i++) {
        const orbContainer = new Container();
        orbContainer.x = orbStartX + i * orbSpacing;
        orbContainer.y = orbY;
        orbContainer.eventMode = 'static';
        orbContainer.cursor = 'pointer';
        overlay.addChild(orbContainer);
        orbContainers.push(orbContainer);

        // Orb circle (unrevealed = cyan glow)
        const orb = new Graphics();
        orb.circle(0, 0, orbRadius);
        orb.fill({ color: 0x1a3a5c });
        orb.stroke({ color: 0x33ccff, width: 3 });
        orbContainer.addChild(orb);
        orbGraphics.push(orb);

        // Question mark
        const qMark = new Text({
          text: '?',
          style: new TextStyle({
            fontFamily: 'Arial Black, Arial',
            fontSize: Math.round(56 * uiScale),
            fill: 0x66ccff,
          }),
        });
        qMark.anchor.set(0.5, 0.5);
        orbContainer.addChild(qMark);

        // Hidden tier label (shown on reveal)
        const tierLabel = new Text({
          text: '',
          style: new TextStyle({
            fontFamily: 'Arial Black, Arial',
            fontSize: Math.round(48 * uiScale),
            fill: 0xffffff,
            stroke: { color: 0x000000, width: Math.round(4 * uiScale) },
          }),
        });
        tierLabel.anchor.set(0.5, 0.5);
        tierLabel.visible = false;
        orbContainer.addChild(tierLabel);
        orbLabels.push(tierLabel);

        // Click handler — each orb can only be clicked once
        const orbIndex = i;
        let clicked = false;

        orbContainer.on('pointerdown', () => {
          if (clicked) return;
          clicked = true;
          orbContainer.cursor = 'default';
          orbContainer.eventMode = 'none';

          const tier = result.picks[orbIndex];
          const tierColor = JACKPOT_PICK_TIER_COLORS[tier] ?? 0xffffff;

          // Reveal: replace question mark with tier number
          qMark.visible = false;
          tierLabel.text = `${tier}`;
          tierLabel.style.fill = tierColor;
          tierLabel.visible = true;

          // Recolor orb
          orb.clear();
          orb.circle(0, 0, orbRadius);
          orb.fill({ color: tierColor, alpha: 0.3 });
          orb.stroke({ color: tierColor, width: 4 });

          picksRevealed++;
          logger.info(`Pick ${picksRevealed}: tier ${tier}`);

          if (picksRevealed >= 3) {
            resolveAllPicks();
          }
        });
      }
    });

    // Wait for all 3 picks
    await allPicksDone;

    // Brief pause before showing result
    await delay(600);

    // ── Highlight matched pair ────────────────────────────────────────────
    if (result.matchedTier > 0) {
      const matchColor = JACKPOT_PICK_TIER_COLORS[result.matchedTier] ?? 0xffffff;

      for (let i = 0; i < 3; i++) {
        if (result.picks[i] === result.matchedTier) {
          // Pulse the matched orbs
          const orb = orbGraphics[i];
          orb.clear();
          orb.circle(0, 0, orbRadius + 6);
          orb.fill({ color: matchColor, alpha: 0.5 });
          orb.stroke({ color: 0xffffff, width: 5 });
        }
      }
    }

    // ── Result text ──────────────────────────────────────────────────────
    const resultY = designH * 0.7;

    if (result.matchedTier > 0) {
      const tierName = JACKPOT_PICK_TIER_NAMES[result.matchedTier] ?? `Tier ${result.matchedTier}`;
      const matchText = new Text({
        text: `MATCHED: ${tierName}`,
        style: new TextStyle({
          fontFamily: 'Arial Black, Arial',
          fontSize: Math.round(36 * uiScale),
          fill: JACKPOT_PICK_TIER_COLORS[result.matchedTier] ?? 0xffffff,
          stroke: { color: 0x000000, width: Math.round(4 * uiScale) },
        }),
      });
      matchText.anchor.set(0.5, 0.5);
      matchText.x = designW / 2;
      matchText.y = resultY;
      overlay.addChild(matchText);

      const prizeText = new Text({
        text: `WIN: $${prize.toFixed(2)}`,
        style: new TextStyle({
          fontFamily: 'Arial Black, Arial',
          fontSize: Math.round(48 * uiScale),
          fill: 0xf7b733,
          stroke: { color: 0x000000, width: Math.round(5 * uiScale) },
          dropShadow: { color: 0x000000, blur: 6, distance: 3, angle: Math.PI / 4 },
        }),
      });
      prizeText.anchor.set(0.5, 0.5);
      prizeText.x = designW / 2;
      prizeText.y = resultY + Math.round(60 * uiScale);
      overlay.addChild(prizeText);
    } else {
      const noMatchText = new Text({
        text: 'NO MATCH',
        style: new TextStyle({
          fontFamily: 'Arial Black, Arial',
          fontSize: Math.round(36 * uiScale),
          fill: 0xaa4444,
          stroke: { color: 0x000000, width: Math.round(4 * uiScale) },
        }),
      });
      noMatchText.anchor.set(0.5, 0.5);
      noMatchText.x = designW / 2;
      noMatchText.y = resultY;
      overlay.addChild(noMatchText);
    }

    // Hold for player to see result
    await delay(3000);

    // ── Cleanup Spine instances ─────────────────────────────────────────
    if (this._spineBg) {
      sm.returnInstance('bonus_pick_background', this._spineBg);
      this._spineBg = null;
    }
    if (this._spineOrbs) {
      sm.returnInstance('bonus_pick_orbs_pick', this._spineOrbs);
      this._spineOrbs = null;
    }

    // ── Cleanup ───────────────────────────────────────────────────────────
    parentContainer.removeChild(overlay);
    overlay.destroy({ children: true });

    this._state.isActive = false;
    logger.info(`Jackpot Pick complete. Prize: $${prize.toFixed(2)}`);

    return prize;
  }

  /** Reset state */
  reset(): void {
    this._state = { picks: [], highestPair: null, prizeMultiplier: 0, isActive: false };
  }
}

/** Simple delay helper */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
